import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Tabs,
  Table,
  Tag,
  Space,
  Button,
  Switch,
  Form,
  Radio,
  Select,
  message,
  Spin,
  Avatar,
  Modal,
  Popconfirm,
  Input,
} from 'antd';
import {
  ArrowRightOutlined,
  SettingOutlined,
  ReloadOutlined,
  SaveOutlined,
  TeamOutlined,
  TagsOutlined,
  ApiOutlined,
  FileTextOutlined,
  ShareAltOutlined,
  UsergroupAddOutlined,
  ControlOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  ticketSettingsApi,
  ticketAgentsApi,
  ticketGroupsApi,
  ticketTypesApi,
  ticketChannelsApi,
  ticketReplyTemplatesApi,
  show422Error,
  ValidationError,
} from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { TicketSettingsSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const TAB_KEYS = [
  { key: 'agents', label: 'نمایندگان', icon: <TeamOutlined /> },
  { key: 'type', label: 'نوع تیکت', icon: <TagsOutlined /> },
  { key: 'channel', label: 'کانال‌ها', icon: <ApiOutlined /> },
  { key: 'reply-template', label: 'قالب پاسخ', icon: <FileTextOutlined /> },
  { key: 'round-robin', label: 'Round Robin', icon: <ShareAltOutlined /> },
  { key: 'group-manage', label: 'گروه‌ها', icon: <UsergroupAddOutlined /> },
  { key: 'ticket-agent-setting', label: 'تنظیم نماینده', icon: <ControlOutlined /> },
];

const scopeOptions = [
  { value: 'all_tickets', label: 'همه تیکت‌ها' },
  { value: 'group_tickets', label: 'تیکت‌های گروه' },
  { value: 'assigned_tickets', label: 'تیکت‌های ارجاع‌شده' },
];

function AgentSettingFormBlock({ form, groups, onSave, saving }) {
  const ticketScope = Form.useWatch('ticket_scope', form);
  return (
    <Form form={form} layout="vertical" className="custom-font max-w-[520px]">
      <Form.Item name="ticket_scope" label="محدوده مشاهده تیکت" rules={[{ required: true }]}>
        <Radio.Group optionType="button" buttonStyle="solid" className="custom-font">
          {scopeOptions.map((o) => (
            <Radio.Button key={o.value} value={o.value}>
              {o.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>
      {ticketScope === 'group_tickets' && (
        <Form.Item
          name="group_id"
          label="گروه‌ها"
          rules={[{ required: true, message: 'حداقل یک گروه انتخاب کنید' }]}
        >
          <Select
            mode="multiple"
            placeholder="انتخاب گروه"
            optionFilterProp="children"
            showSearch
            popupClassName="custom-font"
            className="custom-font"
          >
            {groups.map((g) => (
              <Select.Option key={g.id} value={g.id}>
                {g.group_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <PermissionGate permission="manage_ticket_setting">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={onSave}
          className={PANEL_PRIMARY_BTN_CLASS}
        >
          ذخیره تنظیمات
        </Button>
      </PermissionGate>
    </Form>
  );
}

const TicketSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TAB_KEYS.some((t) => t.key === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'agents';

  const [loading, setLoading] = useState(false);
  const [tabData, setTabData] = useState(null);
  const [companyId, setCompanyId] = useState(() => user?.company_id ?? null);

  const [rrSaving, setRrSaving] = useState(false);
  const [rrStatus, setRrStatus] = useState(0);

  const [agentForm] = Form.useForm();
  const [agentSaving, setAgentSaving] = useState(false);

  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const [addAgentForm] = Form.useForm();
  const watchedAddUserId = Form.useWatch('user_id', addAgentForm);
  const [createEmployees, setCreateEmployees] = useState([]);
  const [agentGroupOptions, setAgentGroupOptions] = useState([]);
  const [loadingCreateMeta, setLoadingCreateMeta] = useState(false);
  const [loadingAgentGroups, setLoadingAgentGroups] = useState(false);
  const [addAgentSubmitting, setAddAgentSubmitting] = useState(false);

  const [editGroupsOpen, setEditGroupsOpen] = useState(false);
  const [editGroupsRow, setEditGroupsRow] = useState(null);
  const [editGroupsForm] = Form.useForm();
  const [editGroupsSubmitting, setEditGroupsSubmitting] = useState(false);
  const [editGroupOptions, setEditGroupOptions] = useState([]);
  const [loadingEditGroupOptions, setLoadingEditGroupOptions] = useState(false);

  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('add');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [ticketGroupForm] = Form.useForm();
  const [groupModalSaving, setGroupModalSaving] = useState(false);
  const [groupDeleteLoadingId, setGroupDeleteLoadingId] = useState(null);

  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeModalMode, setTypeModalMode] = useState('add');
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [ticketTypeForm] = Form.useForm();
  const [typeModalSaving, setTypeModalSaving] = useState(false);
  const [typeDeleteLoadingId, setTypeDeleteLoadingId] = useState(null);
  const [loadingTypeEdit, setLoadingTypeEdit] = useState(false);

  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [channelModalMode, setChannelModalMode] = useState('add');
  const [editingChannelId, setEditingChannelId] = useState(null);
  const [ticketChannelForm] = Form.useForm();
  const [channelModalSaving, setChannelModalSaving] = useState(false);
  const [channelDeleteLoadingId, setChannelDeleteLoadingId] = useState(null);
  const [loadingChannelEdit, setLoadingChannelEdit] = useState(false);

  const [replyTplModalOpen, setReplyTplModalOpen] = useState(false);
  const [replyTplModalMode, setReplyTplModalMode] = useState('add');
  const [editingReplyTplId, setEditingReplyTplId] = useState(null);
  const [replyTemplateForm] = Form.useForm();
  const [replyTplModalSaving, setReplyTplModalSaving] = useState(false);
  const [replyTplDeleteLoadingId, setReplyTplDeleteLoadingId] = useState(null);
  const [loadingReplyTplEdit, setLoadingReplyTplEdit] = useState(false);

  const [replyTplViewOpen, setReplyTplViewOpen] = useState(false);
  const [replyTplViewLoading, setReplyTplViewLoading] = useState(false);
  const [replyTplViewData, setReplyTplViewData] = useState(null);

  const loadTab = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketSettingsApi.getByTab(activeTab);
      const data = res?.data ?? res;
      setTabData(data);

      const cid = user?.company_id ?? data?.ticket_agent_setting?.company_id;
      if (cid != null) setCompanyId(cid);

      if (activeTab === 'round-robin' && data?.round_robin) {
        setRrStatus(Number(data.round_robin.ticket_round_robin_status ?? 0));
      }

      if (activeTab === 'ticket-agent-setting' && data?.ticket_agent_setting) {
        const tas = data.ticket_agent_setting;
        const gids = (tas.group_id || []).map((x) => (typeof x === 'string' ? Number(x) : x));
        agentForm.setFieldsValue({
          ticket_scope: tas.ticket_scope || 'all_tickets',
          group_id: gids.length ? gids : undefined,
        });
      }
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت تنظیمات');
      setTabData(null);
    } finally {
      setLoading(false);
    }
  }, [activeTab, user?.company_id, agentForm]);

  useEffect(() => {
    loadTab();
  }, [loadTab]);

  const handleTabChange = (key) => {
    setSearchParams({ tab: key });
  };

  const saveRoundRobin = async () => {
    if (companyId == null) {
      message.error('شناسه شرکت یافت نشد');
      return;
    }
    setRrSaving(true);
    try {
      await ticketSettingsApi.updateRoundRobinStatus(companyId, {
        request_from_ticket: 'yes',
        ticket_round_robin_status: rrStatus,
      });
      message.success('ذخیره شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setRrSaving(false);
    }
  };

  const saveAgentSettings = async () => {
    if (companyId == null) {
      message.error('شناسه شرکت یافت نشد');
      return;
    }
    try {
      const values = await agentForm.validateFields();
      setAgentSaving(true);
      const scope = values.ticket_scope;
      const payload =
        scope === 'group_tickets'
          ? {
              ticket_scope: scope,
              group_id: (values.group_id || []).map((id) => Number(id)),
            }
          : { ticket_scope: scope };
      await ticketSettingsApi.updateAgentSettings(companyId, payload);
      message.success('تنظیمات ذخیره شد');
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setAgentSaving(false);
    }
  };

  const openAddAgentModal = async () => {
    setAddAgentOpen(true);
    addAgentForm.resetFields();
    setAgentGroupOptions([]);
    setLoadingCreateMeta(true);
    try {
      const res = await ticketAgentsApi.getCreateForm();
      const data = res?.data ?? res;
      setCreateEmployees(data?.employees || []);
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت فرم');
      setAddAgentOpen(false);
    } finally {
      setLoadingCreateMeta(false);
    }
  };

  const loadAgentGroupsForUser = async (userId) => {
    if (userId == null || userId === '') {
      setAgentGroupOptions([]);
      addAgentForm.setFieldsValue({ group_id: undefined });
      return;
    }
    setLoadingAgentGroups(true);
    try {
      const res = await ticketAgentsApi.getAgentGroups(Number(userId));
      const data = res?.data ?? res;
      const groups = data?.groups || [];
      setAgentGroupOptions(groups);
      addAgentForm.setFieldsValue({ group_id: undefined });
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت گروه‌ها');
      setAgentGroupOptions([]);
    } finally {
      setLoadingAgentGroups(false);
    }
  };

  const submitAddAgent = async () => {
    try {
      const values = await addAgentForm.validateFields();
      setAddAgentSubmitting(true);
      await ticketAgentsApi.create({
        user_id: values.user_id,
        group_id: values.group_id || [],
      });
      message.success('نماینده اضافه شد');
      setAddAgentOpen(false);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setAddAgentSubmitting(false);
    }
  };

  const toggleAgentStatus = async (row, enabled) => {
    const userId = row.id;
    const next = enabled ? 'enabled' : 'disabled';
    setStatusUpdatingId(userId);
    try {
      await ticketAgentsApi.updateStatus(userId, { status: next });
      message.success('وضعیت به‌روز شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در به‌روزرسانی');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openEditGroups = async (row) => {
    setEditGroupsRow(row);
    const ids = (row.agent_group || []).map((g) => g.id);
    editGroupsForm.setFieldsValue({ group_id: ids.length ? ids : undefined });
    setEditGroupsOpen(true);
    setLoadingEditGroupOptions(true);
    try {
      const res = await ticketAgentsApi.getCreateForm();
      const data = res?.data ?? res;
      const base = data?.groups || [];
      const merged = new Map(base.map((g) => [g.id, g]));
      (row.agent_group || []).forEach((g) => {
        if (!merged.has(g.id)) merged.set(g.id, g);
      });
      setEditGroupOptions([...merged.values()]);
    } catch (e) {
      console.error(e);
      setEditGroupOptions(row.agent_group || []);
    } finally {
      setLoadingEditGroupOptions(false);
    }
  };

  const submitEditGroups = async () => {
    if (!editGroupsRow) return;
    try {
      const values = await editGroupsForm.validateFields();
      setEditGroupsSubmitting(true);
      await ticketAgentsApi.updateGroups(editGroupsRow.id, {
        group_id: values.group_id || [],
      });
      message.success('گروه‌ها به‌روز شد');
      setEditGroupsOpen(false);
      setEditGroupsRow(null);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setEditGroupsSubmitting(false);
    }
  };

  const deleteAgent = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await ticketAgentsApi.remove(row.id);
      message.success('حذف شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در حذف');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const openTicketGroupModal = (mode, row = null) => {
    setGroupModalMode(mode);
    setEditingGroupId(row?.id ?? null);
    if (mode === 'add') {
      ticketGroupForm.resetFields();
    } else if (row) {
      ticketGroupForm.setFieldsValue({ group_name: row.group_name });
    }
    setGroupModalOpen(true);
  };

  const submitTicketGroup = async () => {
    try {
      const values = await ticketGroupForm.validateFields();
      const name = (values.group_name || '').trim();
      if (!name) {
        message.warning('نام گروه را وارد کنید');
        return;
      }
      setGroupModalSaving(true);
      if (groupModalMode === 'add') {
        await ticketGroupsApi.create({ group_name: name });
        message.success('گروه ثبت شد');
      } else if (editingGroupId != null) {
        await ticketGroupsApi.update(editingGroupId, { group_name: name });
        message.success('گروه به‌روز شد');
      }
      setGroupModalOpen(false);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setGroupModalSaving(false);
    }
  };

  const deleteTicketGroup = async (row) => {
    setGroupDeleteLoadingId(row.id);
    try {
      await ticketGroupsApi.remove(row.id);
      message.success('حذف شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در حذف');
    } finally {
      setGroupDeleteLoadingId(null);
    }
  };

  const openTicketTypeModal = () => {
    setTypeModalMode('add');
    setEditingTypeId(null);
    ticketTypeForm.resetFields();
    setTypeModalOpen(true);
  };

  const openTicketTypeEdit = async (row) => {
    setTypeModalMode('edit');
    setEditingTypeId(row.id);
    setTypeModalOpen(true);
    setLoadingTypeEdit(true);
    try {
      const res = await ticketTypesApi.getEdit(row.id);
      const data = res?.data ?? res;
      const tt = data?.ticket_type;
      ticketTypeForm.setFieldsValue({ type: tt?.type ?? '' });
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت اطلاعات');
      setTypeModalOpen(false);
    } finally {
      setLoadingTypeEdit(false);
    }
  };

  const submitTicketType = async () => {
    try {
      const values = await ticketTypeForm.validateFields();
      const t = (values.type || '').trim();
      if (!t) {
        message.warning('نام نوع را وارد کنید');
        return;
      }
      setTypeModalSaving(true);
      if (typeModalMode === 'add') {
        await ticketTypesApi.create({ type: t });
        message.success('نوع تیکت ثبت شد');
      } else if (editingTypeId != null) {
        await ticketTypesApi.update(editingTypeId, { type: t });
        message.success('نوع تیکت به‌روز شد');
      }
      setTypeModalOpen(false);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setTypeModalSaving(false);
    }
  };

  const deleteTicketType = async (row) => {
    setTypeDeleteLoadingId(row.id);
    try {
      await ticketTypesApi.remove(row.id);
      message.success('حذف شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در حذف');
    } finally {
      setTypeDeleteLoadingId(null);
    }
  };

  const openTicketChannelModal = () => {
    setChannelModalMode('add');
    setEditingChannelId(null);
    ticketChannelForm.resetFields();
    setChannelModalOpen(true);
  };

  const openTicketChannelEdit = async (row) => {
    setChannelModalMode('edit');
    setEditingChannelId(row.id);
    setChannelModalOpen(true);
    setLoadingChannelEdit(true);
    try {
      const res = await ticketChannelsApi.getEdit(row.id);
      const data = res?.data ?? res;
      const ch = data?.ticket_channel;
      ticketChannelForm.setFieldsValue({ channel_name: ch?.channel_name ?? '' });
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت اطلاعات');
      setChannelModalOpen(false);
    } finally {
      setLoadingChannelEdit(false);
    }
  };

  const submitTicketChannel = async () => {
    try {
      const values = await ticketChannelForm.validateFields();
      const name = (values.channel_name || '').trim();
      if (!name) {
        message.warning('نام کانال را وارد کنید');
        return;
      }
      setChannelModalSaving(true);
      if (channelModalMode === 'add') {
        await ticketChannelsApi.create({ channel_name: name });
        message.success('کانال ثبت شد');
      } else if (editingChannelId != null) {
        await ticketChannelsApi.update(editingChannelId, { channel_name: name });
        message.success('کانال به‌روز شد');
      }
      setChannelModalOpen(false);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setChannelModalSaving(false);
    }
  };

  const deleteTicketChannel = async (row) => {
    setChannelDeleteLoadingId(row.id);
    try {
      await ticketChannelsApi.remove(row.id);
      message.success('حذف شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در حذف');
    } finally {
      setChannelDeleteLoadingId(null);
    }
  };

  const openReplyTemplateModal = () => {
    setReplyTplModalMode('add');
    setEditingReplyTplId(null);
    replyTemplateForm.resetFields();
    setReplyTplModalOpen(true);
  };

  const openReplyTemplateEdit = async (row) => {
    setReplyTplModalMode('edit');
    setEditingReplyTplId(row.id);
    setReplyTplModalOpen(true);
    setLoadingReplyTplEdit(true);
    try {
      const res = await ticketReplyTemplatesApi.getEdit(row.id);
      const data = res?.data ?? res;
      const tpl = data?.template;
      replyTemplateForm.setFieldsValue({
        reply_heading: tpl?.reply_heading ?? '',
        description: tpl?.reply_text ?? '',
      });
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت اطلاعات');
      setReplyTplModalOpen(false);
    } finally {
      setLoadingReplyTplEdit(false);
    }
  };

  const submitReplyTemplate = async () => {
    try {
      const values = await replyTemplateForm.validateFields();
      const heading = (values.reply_heading || '').trim();
      const body = (values.description || '').trim();
      if (!heading) {
        message.warning('عنوان را وارد کنید');
        return;
      }
      setReplyTplModalSaving(true);
      if (replyTplModalMode === 'add') {
        await ticketReplyTemplatesApi.create({ reply_heading: heading, description: body });
        message.success('قالب ثبت شد');
      } else if (editingReplyTplId != null) {
        await ticketReplyTemplatesApi.update(editingReplyTplId, {
          reply_heading: heading,
          reply_text: body,
        });
        message.success('قالب به‌روز شد');
      }
      setReplyTplModalOpen(false);
      loadTab();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در ذخیره');
    } finally {
      setReplyTplModalSaving(false);
    }
  };

  const deleteReplyTemplate = async (row) => {
    setReplyTplDeleteLoadingId(row.id);
    try {
      await ticketReplyTemplatesApi.remove(row.id);
      message.success('حذف شد');
      loadTab();
    } catch (e) {
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, agentForm);
      else message.error(e?.message || 'خطا در حذف');
    } finally {
      setReplyTplDeleteLoadingId(null);
    }
  };

  const openReplyTemplateView = async (row) => {
    setReplyTplViewOpen(true);
    setReplyTplViewData(null);
    setReplyTplViewLoading(true);
    try {
      const res = await ticketReplyTemplatesApi.getById(row.id);
      const data = res?.data ?? res;
      setReplyTplViewData(data?.template ?? null);
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'خطا در دریافت قالب');
      setReplyTplViewOpen(false);
    } finally {
      setReplyTplViewLoading(false);
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return <TicketSettingsSkeleton />;
    }

    switch (activeTab) {
      case 'agents': {
        const rows = tabData?.ticket_agents || [];
        const columns = [
          {
            title: 'نماینده',
            key: 'user',
            render: (_, row) => (
              <Space>
                <Avatar src={getImagePath(row.image_url)} size={40}>
                  {(row.name || '?').charAt(0)}
                </Avatar>
                <span style={{ fontFamily: 'CustomFont' }}>{row.name}</span>
              </Space>
            ),
          },
          {
            title: 'گروه‌ها',
            key: 'groups',
            render: (_, row) => (
              <Space wrap>
                {(row.agent_group || []).map((g) => (
                  <Tag key={g.id}>{g.group_name}</Tag>
                ))}
              </Space>
            ),
          },
          {
            title: 'وضعیت',
            key: 'agent',
            width: 130,
            render: (_, row) => {
              const st = row.agent?.[0]?.status;
              if (!st) return '—';
              return (
                <PermissionGate
                  permission="manage_ticket_setting"
                  fallback={
                    <Tag color={st === 'enabled' ? 'green' : 'default'}>
                      {st === 'enabled' ? 'فعال' : 'غیرفعال'}
                    </Tag>
                  }
                >
                  <Switch
                    checked={st === 'enabled'}
                    loading={statusUpdatingId === row.id}
                    onChange={(checked) => toggleAgentStatus(row, checked)}
                  />
                </PermissionGate>
              );
            },
          },
          {
            title: 'عملیات',
            key: 'actions',
            width: 160,
            render: (_, row) => (
              <PermissionGate
                permission="manage_ticket_setting"
                fallback={<span className="text-gray-400 text-sm">—</span>}
              >
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditGroups(row)}
                  >
                    گروه‌ها
                  </Button>
                  <Popconfirm
                    title="حذف نماینده از گروه‌های تیکت؟"
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => deleteAgent(row)}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deleteLoadingId === row.id}
                    >
                      حذف
                    </Button>
                  </Popconfirm>
                </Space>
              </PermissionGate>
            ),
          },
        ];
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <PermissionGate permission="manage_ticket_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openAddAgentModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن نماینده
                </Button>
              </PermissionGate>
            </div>
            <Table rowKey={(r) => r.id} columns={columns} dataSource={rows} pagination={{ pageSize: 8 }} />
          </div>
        );
      }
      case 'type': {
        const typeRows = tabData?.ticket_types || [];
        const typeColumns = [
          {
            title: 'نام نوع',
            dataIndex: 'type',
            key: 'type',
            render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>,
          },
          {
            title: 'عملیات',
            key: 'actions',
            width: 160,
            render: (_, row) => (
              <PermissionGate
                permission="manage_ticket_setting"
                fallback={<span className="text-gray-400 text-sm">—</span>}
              >
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openTicketTypeEdit(row)}
                  >
                    
                  </Button>
                  <Popconfirm
                    title="حذف این نوع تیکت؟"
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => deleteTicketType(row)}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={typeDeleteLoadingId === row.id}
                    >
                      
                    </Button>
                  </Popconfirm>
                </Space>
              </PermissionGate>
            ),
          },
        ];
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <PermissionGate permission="manage_ticket_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openTicketTypeModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن نوع تیکت
                </Button>
              </PermissionGate>
            </div>
            <Table rowKey="id" columns={typeColumns} dataSource={typeRows} pagination={{ pageSize: 10 }} />
          </div>
        );
      }
      case 'channel': {
        const channelRows = tabData?.ticket_channels || [];
        const channelColumns = [
          {
            title: 'نام کانال',
            dataIndex: 'channel_name',
            key: 'channel_name',
            render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>,
          },
          {
            title: 'عملیات',
            key: 'actions',
            width: 160,
            render: (_, row) => (
              <PermissionGate
                permission="manage_ticket_setting"
                fallback={<span className="text-gray-400 text-sm">—</span>}
              >
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openTicketChannelEdit(row)}
                  >
                    
                  </Button>
                  <Popconfirm
                    title="حذف این کانال؟"
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => deleteTicketChannel(row)}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={channelDeleteLoadingId === row.id}
                    >
                      
                    </Button>
                  </Popconfirm>
                </Space>
              </PermissionGate>
            ),
          },
        ];
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <PermissionGate permission="manage_ticket_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openTicketChannelModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن کانال
                </Button>
              </PermissionGate>
            </div>
            <Table rowKey="id" columns={channelColumns} dataSource={channelRows} pagination={{ pageSize: 10 }} />
          </div>
        );
      }
      case 'reply-template': {
        const tplRows = tabData?.ticket_reply_templates || [];
        const tplColumns = [
          { title: 'عنوان', dataIndex: 'reply_heading', key: 'h', ellipsis: true },
          { title: 'متن', dataIndex: 'reply_text', key: 't', ellipsis: true },
          {
            title: 'عملیات',
            key: 'actions',
            width: 220,
            render: (_, row) => (
              <Space wrap>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => openReplyTemplateView(row)}
                >
                  مشاهده
                </Button>
                <PermissionGate
                  permission="manage_ticket_setting"
                  fallback={<span className="text-gray-400 text-sm">—</span>}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openReplyTemplateEdit(row)}
                  >
                    
                  </Button>
                  <Popconfirm
                    title="حذف این قالب؟"
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => deleteReplyTemplate(row)}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={replyTplDeleteLoadingId === row.id}
                    >
                      
                    </Button>
                  </Popconfirm>
                </PermissionGate>
              </Space>
            ),
          },
        ];
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <PermissionGate permission="manage_ticket_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openReplyTemplateModal}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن قالب پاسخ
                </Button>
              </PermissionGate>
            </div>
            <Table rowKey="id" columns={tplColumns} dataSource={tplRows} pagination={{ pageSize: 8 }} />
          </div>
        );
      }
      case 'round-robin':
        return (
          <div className="max-w-md space-y-4">
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <span style={{ fontFamily: 'CustomFont' }}>فعال‌سازی Round Robin</span>
              <Switch checked={rrStatus === 1} onChange={(c) => setRrStatus(c ? 1 : 0)} />
            </div>
            <PermissionGate permission="manage_ticket_setting">
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={rrSaving}
                onClick={saveRoundRobin}
                className={PANEL_PRIMARY_BTN_CLASS}
              >
                ذخیره
              </Button>
            </PermissionGate>
          </div>
        );
      case 'group-manage': {
        const groupRows = tabData?.ticket_groups || [];
        const groupColumns = [
          {
            title: 'نام گروه',
            dataIndex: 'group_name',
            key: 'group_name',
            render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>,
          },
          {
            title: 'عملیات',
            key: 'actions',
            width: 160,
            render: (_, row) => (
              <PermissionGate
                permission="manage_ticket_setting"
                fallback={<span className="text-gray-400 text-sm">—</span>}
              >
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openTicketGroupModal('edit', row)}
                  >
                    
                  </Button>
                  <Popconfirm
                    title="حذف این گروه؟"
                    okText="بله"
                    cancelText="خیر"
                    onConfirm={() => deleteTicketGroup(row)}
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={groupDeleteLoadingId === row.id}
                    >
                      
                    </Button>
                  </Popconfirm>
                </Space>
              </PermissionGate>
            ),
          },
        ];
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              <PermissionGate permission="manage_ticket_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openTicketGroupModal('add')}
                  className={PANEL_PRIMARY_BTN_CLASS}
                >
                  افزودن گروه
                </Button>
              </PermissionGate>
            </div>
            <Table rowKey="id" columns={groupColumns} dataSource={groupRows} pagination={{ pageSize: 10 }} />
          </div>
        );
      }
      case 'ticket-agent-setting':
        return (
          <AgentSettingFormBlock
            form={agentForm}
            groups={tabData?.groups || []}
            onSave={saveAgentSettings}
            saving={agentSaving}
          />
        );
      default:
        return null;
    }
  };

  const tabBarItems = TAB_KEYS.map((t) => ({
    key: t.key,
    label: (
      <span className="inline-flex items-center gap-1 custom-font">
        {t.icon}
        <span>{t.label}</span>
      </span>
    ),
  }));

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center">
                <SettingOutlined className="text-sky-600 dark:text-sky-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">تنظیمات تیکت</h1>
                {/* <p className="text-gray-500 dark:text-gray-400 text-sm m-0">نمایندگان، انواع، کانال‌ها، قالب‌ها و سایر موارد</p> */}
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={loadTab} loading={loading}>
                بروزرسانی
              </Button>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/tickets')}>
                بازگشت به تیکت‌ها
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabBarItems} type="card" />
            <div className="mt-6 min-h-[240px]">{renderTabContent()}</div>
          </div>
        </Card>

        <Modal
          title="افزودن نماینده"
          open={addAgentOpen}
          onCancel={() => setAddAgentOpen(false)}
          onOk={submitAddAgent}
          confirmLoading={addAgentSubmitting}
          destroyOnClose
          okText="ثبت"
          cancelText="انصراف"
          width={480}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingCreateMeta}>
            <Form form={addAgentForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
              <Form.Item name="user_id" label="کارمند" rules={[{ required: true, message: 'کارمند را انتخاب کنید' }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder="انتخاب کارمند"
                  onChange={(v) => loadAgentGroupsForUser(v)}
                  allowClear
                >
                  {createEmployees.map((e) => (
                    <Select.Option key={e.id} value={e.id}>
                      {e.name} — {e.email}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="group_id"
                label="گروه‌ها"
                rules={[{ required: true, message: 'حداقل یک گروه انتخاب کنید' }]}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    loadingAgentGroups
                      ? 'در حال بارگذاری...'
                      : watchedAddUserId
                        ? 'انتخاب گروه'
                        : 'ابتدا کارمند را انتخاب کنید'
                  }
                  optionFilterProp="children"
                  showSearch
                  disabled={!watchedAddUserId}
                  loading={loadingAgentGroups}
                >
                  {agentGroupOptions.map((g) => (
                    <Select.Option key={g.id} value={g.id}>
                      {g.group_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title="ویرایش گروه‌های نماینده"
          open={editGroupsOpen}
          onCancel={() => {
            setEditGroupsOpen(false);
            setEditGroupsRow(null);
          }}
          onOk={submitEditGroups}
          confirmLoading={editGroupsSubmitting}
          destroyOnClose
          okText="ذخیره"
          cancelText="انصراف"
          width={480}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingEditGroupOptions}>
            <Form form={editGroupsForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
              <Form.Item name="group_id" label="گروه‌ها">
                <Select
                  mode="multiple"
                  placeholder="گروه‌ها را انتخاب کنید"
                  optionFilterProp="children"
                  showSearch
                  allowClear
                >
                  {editGroupOptions.map((g) => (
                    <Select.Option key={g.id} value={g.id}>
                      {g.group_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={groupModalMode === 'add' ? 'افزودن گروه تیکت' : 'ویرایش گروه تیکت'}
          open={groupModalOpen}
          onCancel={() => setGroupModalOpen(false)}
          onOk={submitTicketGroup}
          confirmLoading={groupModalSaving}
          destroyOnClose
          okText="ذخیره"
          cancelText="انصراف"
          width={440}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Form form={ticketGroupForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
            <Form.Item
              name="group_name"
              label="نام گروه"
              rules={[{ required: true, message: 'نام گروه را وارد کنید' }]}
            >
              <Input placeholder="مثلاً پشتیبانی فنی" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={typeModalMode === 'add' ? 'افزودن نوع تیکت' : 'ویرایش نوع تیکت'}
          open={typeModalOpen}
          onCancel={() => setTypeModalOpen(false)}
          onOk={submitTicketType}
          confirmLoading={typeModalSaving}
          destroyOnClose
          okText="ذخیره"
          cancelText="انصراف"
          width={440}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingTypeEdit}>
            <Form form={ticketTypeForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
              <Form.Item name="type" label="نام نوع" rules={[{ required: true, message: 'نام نوع را وارد کنید' }]}>
                <Input placeholder="مثلاً خطا در خرید" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={channelModalMode === 'add' ? 'افزودن کانال' : 'ویرایش کانال'}
          open={channelModalOpen}
          onCancel={() => setChannelModalOpen(false)}
          onOk={submitTicketChannel}
          confirmLoading={channelModalSaving}
          destroyOnClose
          okText="ذخیره"
          cancelText="انصراف"
          width={440}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingChannelEdit}>
            <Form form={ticketChannelForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
              <Form.Item
                name="channel_name"
                label="نام کانال"
                rules={[{ required: true, message: 'نام کانال را وارد کنید' }]}
              >
                <Input placeholder="مثلاً اینستاگرام" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title={replyTplModalMode === 'add' ? 'افزودن قالب پاسخ' : 'ویرایش قالب پاسخ'}
          open={replyTplModalOpen}
          onCancel={() => setReplyTplModalOpen(false)}
          onOk={submitReplyTemplate}
          confirmLoading={replyTplModalSaving}
          destroyOnClose
          okText="ذخیره"
          cancelText="انصراف"
          width={520}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        >
          <Spin spinning={loadingReplyTplEdit}>
            <Form form={replyTemplateForm} layout="vertical" style={{ marginTop: 16, fontFamily: 'CustomFont' }}>
              <Form.Item
                name="reply_heading"
                label="عنوان"
                rules={[{ required: true, message: 'عنوان را وارد کنید' }]}
              >
                <Input placeholder="مثلاً تیکت تماس پشتیبانی" />
              </Form.Item>
              <Form.Item name="description" label="متن / توضیحات">
                <Input.TextArea rows={5} placeholder="متن قالب پاسخ" />
              </Form.Item>
            </Form>
          </Spin>
        </Modal>

        <Modal
          title="مشاهده قالب پاسخ"
          open={replyTplViewOpen}
          onCancel={() => {
            setReplyTplViewOpen(false);
            setReplyTplViewData(null);
          }}
          footer={
            <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} onClick={() => setReplyTplViewOpen(false)}>
              بستن
            </Button>
          }
          width={560}
          destroyOnClose
        >
          <Spin spinning={replyTplViewLoading}>
            {replyTplViewData ? (
              <div className="space-y-3" style={{ fontFamily: 'CustomFont' }}>
                <div>
                  <span className="text-gray-500 text-sm">عنوان</span>
                  <p className="m-0 mt-1 font-medium">{replyTplViewData.reply_heading}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">متن</span>
                  <p className="m-0 mt-1 whitespace-pre-wrap">{replyTplViewData.reply_text}</p>
                </div>
              </div>
            ) : !replyTplViewLoading ? (
              <p className="text-gray-400 m-0">داده‌ای نیست</p>
            ) : null}
          </Spin>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TicketSettings;
